import * as React from 'react';

import { refreshDataSourceTables } from 'src/actions/data-sources/action-creators/refreshDataSourceTables';
import { PanelProps } from 'src/components/panelStack/PanelStack';
import { DataSourceType } from 'src/constants/dataSourceTypeDescription';
import { ConnectionSelectionStep } from 'src/containers/ProjectNewResourceDialog/components/ConnectionSelectionStep';
import { useProjectVirtualGraphPreviewDialog } from 'src/containers/ProjectVirtualGraphPreviewDialog/context/useProjectVirtualGraphPreviewDialog';
import { DataSourceCreationPanel } from 'src/containers/ProjectVirtualGraphPreviewDialog/panels/DataSourceCreationPanel';
import { connectionSelector } from 'src/selectors/connection';
import { dataSourcesByTypeSelector } from 'src/selectors/dataSourcesByType';
import { DataSource } from 'src/store/state/dataSources';
import * as copy from 'src/templates/copy';
import { useDialogPanelSetup } from 'src/utils/hooks/useDialogPanelSetup';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export type ConnectionSelectionPanelProps = {
  dataSourceType: DataSourceType;
};

export const ConnectionSelectionPanel: React.VFC<
  PanelProps<ConnectionSelectionPanelProps>
> = ({ closePanel, closeToRoot, openPanel, dataSourceType }) => {
  const dispatch = useReduxDispatch();
  const {
    setDialogTitle,
    setHeaderBackButton,
    setFullSize,
    setFooterOptions,
    localVirtualGraph,
    updateVirtualGraphDataSource,
  } = useProjectVirtualGraphPreviewDialog();

  const connection = useReduxSelector(connectionSelector);
  const dataSourcesByType = useReduxSelector(dataSourcesByTypeSelector);

  const [isConnecting, setIsConnecting] = React.useState(false);
  const isMounted = useIsMounted();

  const dataSources = React.useMemo(
    () => dataSourcesByType[dataSourceType] || [],
    [dataSourcesByType, dataSourceType]
  );

  const handleBack = React.useCallback(() => {
    closePanel?.();
  }, [closePanel]);

  const handleSelectNewConnection = React.useCallback(() => {
    openPanel?.({
      renderPanel: DataSourceCreationPanel,
      props: { dataSourceType },
    });
  }, [openPanel, dataSourceType]);

  const handleSelectDataSource = React.useCallback(
    async (dataSource: DataSource) => {
      if (!connection || !localVirtualGraph || isConnecting) {
        return;
      }

      setIsConnecting(true);

      try {
        await dispatch(refreshDataSourceTables(dataSource.id));

        updateVirtualGraphDataSource({
          connectionId: connection.id || null,
          dataSourceEndpoint: dataSource.options?.['jdbc.url'],
          dataSourceId: dataSource.id,
          dataSourceType: dataSource.type,
        });

        // Return to the overview panel
        closeToRoot?.();
      } finally {
        if (isMounted.current) {
          setIsConnecting(false);
        }
      }
    },
    [
      connection,
      closeToRoot,
      dispatch,
      isConnecting,
      isMounted,
      localVirtualGraph,
      updateVirtualGraphDataSource,
    ]
  );

  useDialogPanelSetup(
    { setDialogTitle, setHeaderBackButton, setFullSize, setFooterOptions },
    {
      title:
        copy.components.virtualGraphPreviewDialog.connectionSelection.title,
      fullSize: false,
      onBack: handleBack,
    }
  );

  return (
    <ConnectionSelectionStep
      dataSources={dataSources}
      dataSourceType={dataSourceType}
      isLoading={isConnecting}
      onSelectDataSource={handleSelectDataSource}
      onSelectNewConnection={handleSelectNewConnection}
    />
  );
};
